import React, { useMemo } from 'react';

import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';

import { formatDateSlashesWithClock } from '~/services/TimeService';

import type { ParityNotificationRecord } from '~/types/services/parityNotifications';

interface ParityNotificationsGridProps {
  notifications: ParityNotificationRecord[];
  isLoading: boolean;
  error: Error | null;
  onSendEmail: (id: string) => void;
  sendingId: string | null;
}

function buildParityNotificationColumns(
  onSendEmail: (id: string) => void,
  sendingId: string | null,
): GridColDef<ParityNotificationRecord>[] {
  return [
    { field: 'id', headerName: 'ID', flex: 2 },
    { field: 'fk_vendor_id', headerName: 'Vendor ID', flex: 1.5 },
    { field: 'fk_salesforce_id', headerName: 'Opportunity ID', flex: 1.5 },
    {
      field: 'offerId',
      headerName: 'Offer ID',
      flex: 1.5,
      valueGetter: (_value, row) => row.metadata?.offerId ?? '',
    },
    {
      field: 'parityCategory',
      headerName: 'Category',
      flex: 1,
      valueGetter: (_value, row) => row.metadata?.parityCategory ?? '',
    },
    {
      field: 'ratePlanId',
      headerName: 'Rate Plan ID',
      flex: 1.5,
      valueGetter: (_value, row) => row.metadata?.ratePlanId ?? '',
    },
    {
      field: 'parityValue',
      headerName: 'Parity Value',
      flex: 1,
      valueGetter: (_value, row) => row.metadata?.parityValue ?? '',
    },
    {
      field: 'parityUplift',
      headerName: 'Uplift',
      flex: 0.75,
      valueGetter: (_value, row) => row.metadata?.parityUplift ?? '',
    },
    {
      field: 'parityItems',
      headerName: 'Parity Weeks',
      flex: 0.75,
      valueGetter: (_value, row) => row.metadata?.parityItems?.length ?? 0,
    },
    { field: 'status', headerName: 'Status', flex: 0.75 },
    {
      field: 'created_at',
      headerName: 'Created At',
      flex: 1.5,
      valueFormatter: (value) => (value ? formatDateSlashesWithClock(value) : ''),
    },
    {
      field: 'actions',
      headerName: '',
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => (
        <Button size="small" variant="text" disabled={sendingId === row.id} onClick={() => onSendEmail(row.id)}>
          {sendingId === row.id ? <CircularProgress size={14} /> : 'Send Email'}
        </Button>
      ),
    },
  ];
}

export default function ParityNotificationsGrid({
  notifications,
  isLoading,
  error,
  onSendEmail,
  sendingId,
}: ParityNotificationsGridProps) {
  const columns = useMemo(() => buildParityNotificationColumns(onSendEmail, sendingId), [onSendEmail, sendingId]);

  return (
    <Box mt={4}>
      <Typography variant="h6" mb={1}>
        Unsolved Price Parity Notifications
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message}
        </Alert>
      )}

      <DataGrid
        autoHeight
        columns={columns}
        rows={notifications}
        loading={isLoading}
        getRowId={(row) => row.id}
        disableRowSelectionOnClick
        disableColumnMenu
      />
    </Box>
  );
}
